import { Suspense, lazy, useEffect, useState } from "react";
import type { ComponentType, ReactNode } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { parseFypAtPath, type FypAtView } from "@shared/fypProfile";
import { parseAffiliateJoinPath } from "@shared/affiliateJoin";
import { parseInvitePath } from "@shared/affiliateInvite";
import type { PublicPage } from "@shared/types";
import { bantuanAsked } from "@shared/supportDesk";
import { cachedHomepage, ensureHomepage } from "@/lib/homepage";
import type { HomepageResolution } from "@/lib/homepage";
import {
  ProtectedRoute,
  PublicOnlyRoute,
  CustomerRoute,
  VerifiedRoute,
  AffiliateRoute,
  AdminRoute,
  ResellerRoute,
} from "@/components/RouteGuards";
import { StoreProvider } from "@/context/StoreContext";
import { PlatformPixel } from "@/components/PlatformPixel";
import { ApexRedirect } from "@/components/ApexRedirect";
import { SetPasswordGate } from "@/components/SetPasswordGate";
import { isStorefrontHost } from "@/lib/host";
import { prefetchBuyerFunnelOnIdle } from "@/lib/prefetch";
import { loadChunk } from "@/lib/chunkLoader";
import { funnelBoot, salesOpenedAfterFyp } from "@/lib/funnelBoot";
import { inSheet } from "@/lib/fypSheet";
import { FypBootFallback } from "@/components/fyp/FypBootFallback";
import type { FunnelBoot, PublicFunnelFyp } from "@shared/funnelFyp";
import { FullScreenSpinner } from "@/components/ui/FullScreenSpinner";
import { loadFullMessages } from "@/i18n/core";
import { MOVED_ROUTES, MovedRoute } from "@/routes/movedRoutes";

// Route-based code splitting: each page ships as its own chunk so the initial
// load only fetches the shell + the page being viewed, not the whole app. Pages
// are named exports, so map the chosen export onto `default` for React.lazy.
//
// A page also waits for the app's full UI copy (loadFullMessages), fetched in
// parallel with its own chunk, so it never renders a raw key.
function lazyNamed<P extends object = Record<string, never>>(
  loader: () => Promise<Record<string, unknown>>,
  name: string,
) {
  return lazy(() =>
    loadChunk(() => Promise.all([loader(), loadFullMessages()]).then(([m]) => m)).then((m) => ({
      default: m[name] as ComponentType<P>,
    })),
  );
}

// The pages a buyer lands on from an ad: every word they show is in the
// storefront copy the entry already carries (tests/unit/i18nStorefrontCopy.test.ts
// holds them to it), and fetching the dashboard's strings would only hold the
// sales page back.
function lazyBuyer<P extends object = Record<string, never>>(
  loader: () => Promise<Record<string, unknown>>,
  name: string,
) {
  return lazy(() => loadChunk(loader).then((m) => ({ default: m[name] as ComponentType<P> })));
}

// Dashboard + admin chrome (sidebar/topbar/store context). Buyers and apex
// visitors never mount these, so keep them out of the entry chunk — they load on
// the first /dashboard or /admin navigation, behind the auth guard.
const AppLayout = lazyNamed(() => import("@/components/layout/AppLayout"), "AppLayout");
// Not `lazyNamed`: that helper erases props, and the hub is configured per route.
const TabbedHub = lazy(() =>
  loadChunk(() => Promise.all([import("@/components/layout/TabbedHub"), loadFullMessages()]).then(([m]) => m)).then(
    (m) => ({ default: m.TabbedHub }),
  ),
);
const AdminLayout = lazyNamed(() => import("@/components/layout/AdminLayout"), "AdminLayout");
const ResellerLayout = lazyNamed(() => import("@/components/layout/ResellerLayout"), "ResellerLayout");
const ResellerOverviewPage = lazyNamed(() => import("@/pages/reseller/ResellerOverviewPage"), "ResellerOverviewPage");
const ResellerSellersPage = lazyNamed(() => import("@/pages/reseller/ResellerSellersPage"), "ResellerSellersPage");
const ResellerSitePage = lazyNamed(() => import("@/pages/reseller/ResellerSitePage"), "ResellerSitePage");
const ResellerOrdersPage = lazyNamed(() => import("@/pages/reseller/ResellerOrdersPage"), "ResellerOrdersPage");
const ResellerCustomersPage = lazyNamed(() => import("@/pages/reseller/ResellerCustomersPage"), "ResellerCustomersPage");
const ResellerPaymentPage = lazyNamed(() => import("@/pages/reseller/ResellerPaymentPage"), "ResellerPaymentPage");
const ResellerPlansPage = lazyNamed(() => import("@/pages/reseller/ResellerPlansPage"), "ResellerPlansPage");

const AdminOverviewPage = lazyNamed(() => import("@/pages/admin/AdminOverviewPage"), "AdminOverviewPage");
const AdminResellersPage = lazyNamed(() => import("@/pages/admin/AdminResellersPage"), "AdminResellersPage");
const AdminResellerManagePage = lazyNamed(
  () => import("@/pages/admin/AdminResellerManagePage"),
  "AdminResellerManagePage",
);
const AdminSellersPage = lazyNamed(() => import("@/pages/admin/AdminSellersPage"), "AdminSellersPage");
const AdminSitesPage = lazyNamed(() => import("@/pages/admin/AdminSitesPage"), "AdminSitesPage");
const AdminSiteManagePage = lazyNamed(() => import("@/pages/admin/AdminSiteManagePage"), "AdminSiteManagePage");
const AdminWpImportPage = lazyNamed(() => import("@/pages/admin/AdminWpImportPage"), "AdminWpImportPage");
const AdminPageInsightsPage = lazyNamed(() => import("@/pages/admin/AdminPageInsightsPage"), "AdminPageInsightsPage");
const AdminAdsPage = lazyNamed(() => import("@/pages/admin/AdminAdsPage"), "AdminAdsPage");
const AdminFunnelsPage = lazyNamed(() => import("@/pages/admin/AdminFunnelsPage"), "AdminFunnelsPage");
const AdminCashflowPage = lazyNamed(() => import("@/pages/admin/AdminCashflowPage"), "AdminCashflowPage");
const AdminMonetizePage = lazyNamed(() => import("@/pages/admin/AdminMonetizePage"), "AdminMonetizePage");
const AdminFunnelNewPage = lazyNamed(() => import("@/pages/admin/AdminFunnelNewPage"), "AdminFunnelNewPage");
const AdminFunnelPage = lazyNamed(() => import("@/pages/admin/AdminFunnelPage"), "AdminFunnelPage");
const AdminAdLinksPage = lazyNamed(() => import("@/pages/admin/AdminAdLinksPage"), "AdminAdLinksPage");
const AdminDomainsPage = lazyNamed(() => import("@/pages/admin/AdminDomainsPage"), "AdminDomainsPage");
const AdminCfAccountsPage = lazyNamed(() => import("@/pages/admin/AdminCfAccountsPage"), "AdminCfAccountsPage");
const AdminOrdersPage = lazyNamed(() => import("@/pages/admin/AdminOrdersPage"), "AdminOrdersPage");
const AdminCustomersPage = lazyNamed(() => import("@/pages/admin/AdminCustomersPage"), "AdminCustomersPage");
const AdminPaymentsPage = lazyNamed(() => import("@/pages/admin/AdminPaymentsPage"), "AdminPaymentsPage");
const AdminModerationPage = lazyNamed(() => import("@/pages/admin/AdminModerationPage"), "AdminModerationPage");
const AdminAuditPage = lazyNamed(() => import("@/pages/admin/AdminAuditPage"), "AdminAuditPage");
const AdminPlansPage = lazyNamed(() => import("@/pages/admin/AdminPlansPage"), "AdminPlansPage");
const AdminMembershipPage = lazyNamed(() => import("@/pages/admin/AdminMembershipPage"), "AdminMembershipPage");
const AdminSettingsPage = lazyNamed(() => import("@/pages/admin/AdminSettingsPage"), "AdminSettingsPage");
const AdminSupportPage = lazyNamed(() => import("@/pages/admin/AdminSupportPage"), "AdminSupportPage");
const AdminAffiliatesPage = lazyNamed(() => import("@/pages/admin/AdminAffiliatesPage"), "AdminAffiliatesPage");

const LandingPage = lazyNamed(() => import("@/pages/LandingPage"), "LandingPage");
const SalesPage = lazyNamed(() => import("@/pages/SalesPage"), "SalesPage");
const PricingPage = lazyNamed(() => import("@/pages/PricingPage"), "PricingPage");
const ContactPage = lazyNamed(() => import("@/pages/public/ContactPage"), "ContactPage");
const CoachingJoinPage = lazyNamed(() => import("@/pages/coaching/CoachingJoinPage"), "CoachingJoinPage");
const CoachingRoomPage = lazyNamed(() => import("@/pages/coaching/CoachingRoomPage"), "CoachingRoomPage");
const BantuanChat = lazyNamed<{ surface: "home" | "contact" | "account" | "store" }>(() => import("@/components/bantuan/BantuanChat"), "BantuanChat");
const ProjectsPage = lazyNamed(() => import("@/pages/ProjectsPage"), "ProjectsPage");
const ProjectRegisterPage = lazyNamed(
  () => import("@/pages/ProjectRegisterPage"),
  "ProjectRegisterPage",
);
const SubscribePage = lazyNamed(() => import("@/pages/SubscribePage"), "SubscribePage");
const ResellerBuyPage = lazyNamed(() => import("@/pages/ResellerBuyPage"), "ResellerBuyPage");
const SubscribedPage = lazyNamed(() => import("@/pages/SubscribedPage"), "SubscribedPage");
const InvitePage = lazyNamed(() => import("@/pages/InvitePage"), "InvitePage");
const OAuthAuthorizePage = lazyNamed(() => import("@/pages/OAuthAuthorizePage"), "OAuthAuthorizePage");
const LoginPage = lazyNamed(() => import("@/pages/auth/LoginPage"), "LoginPage");
const RegisterPage = lazyNamed(() => import("@/pages/auth/RegisterPage"), "RegisterPage");
const JoinChatPage = lazyNamed(() => import("@/pages/auth/JoinChatPage"), "JoinChatPage");
const VerifyEmailPage = lazyNamed(() => import("@/pages/auth/VerifyEmailPage"), "VerifyEmailPage");
const ForgotPasswordPage = lazyNamed(() => import("@/pages/auth/ForgotPasswordPage"), "ForgotPasswordPage");
const ResetPasswordPage = lazyNamed(() => import("@/pages/auth/ResetPasswordPage"), "ResetPasswordPage");

const AccountLoginPage = lazyNamed(() => import("@/pages/account/AccountLoginPage"), "AccountLoginPage");
const RoleChooserPage = lazyNamed(() => import("@/pages/auth/RoleChooserPage"), "RoleChooserPage");
const AccountResetPage = lazyNamed(() => import("@/pages/account/AccountResetPage"), "AccountResetPage");
const AccountPage = lazyNamed(() => import("@/pages/account/AccountPage"), "AccountPage");
const LmsView = lazyNamed(() => import("@/pages/account/LmsView"), "LmsView");
const CertificatePage = lazyNamed(() => import("@/pages/account/CertificatePage"), "CertificatePage");
const AccountCertificatePage = lazyNamed(
  () => import("@/pages/account/AccountCertificatePage"),
  "AccountCertificatePage",
);
const EbookOpenPage = lazyNamed(() => import("@/pages/account/EbookOpenPage"), "EbookOpenPage");
const AffiliatePage = lazyNamed(() => import("@/pages/AffiliatePage"), "AffiliatePage");
const AffiliateLayout = lazyNamed(() => import("@/components/layout/AffiliateLayout"), "AffiliateLayout");
const AffiliateOverviewPage = lazyNamed(() => import("@/pages/affiliate/AffiliateOverviewPage"), "AffiliateOverviewPage");
const AffiliateLinksPage = lazyNamed(() => import("@/pages/affiliate/AffiliateLinksPage"), "AffiliateLinksPage");
const AffiliateEarningsPage = lazyNamed(() => import("@/pages/affiliate/AffiliateEarningsPage"), "AffiliateEarningsPage");
const AffiliateTrafficPage = lazyNamed(() => import("@/pages/affiliate/AffiliateTrafficPage"), "AffiliateTrafficPage");
const AffiliateBankPage = lazyNamed(() => import("@/pages/affiliate/AffiliateBankPage"), "AffiliateBankPage");
const AffiliateStoresPage = lazyNamed(() => import("@/pages/affiliate/AffiliateStoresPage"), "AffiliateStoresPage");
const AffiliateAssetsPage = lazyNamed(() => import("@/pages/affiliate/AffiliateAssetsPage"), "AffiliateAssetsPage");
const AffiliateLeaderboardPage = lazyNamed(() => import("@/pages/affiliate/AffiliateLeaderboardPage"), "AffiliateLeaderboardPage");
const AffiliateTermsPage = lazyNamed(() => import("@/pages/affiliate/AffiliateTermsPage"), "AffiliateTermsPage");
const AffiliatePixelPage = lazyNamed(() => import("@/pages/affiliate/AffiliatePixelPage"), "AffiliatePixelPage");

const OverviewPage = lazyNamed(() => import("@/pages/dashboard/OverviewPage"), "OverviewPage");
const LiveSalesPage = lazyNamed(() => import("@/pages/LiveSalesPage"), "LiveSalesPage");
const PublicLivePage = lazyNamed(() => import("@/pages/PublicLivePage"), "PublicLivePage");
const KelasRoomPage = lazyBuyer(() => import("@/pages/public/KelasRoomPage"), "KelasRoomPage");
const PublicFypPage = lazyNamed(() => import("@/pages/PublicFypPage"), "PublicFypPage");
const FypAwamPage = lazyNamed(() => import("@/pages/PublicFypPage"), "FypAwamPage");
const PublicFypProfilePage = lazyNamed<{ at: FypAtView }>(() => import("@/pages/PublicFypProfilePage"), "PublicFypProfilePage");
const PublicAffiliateJoinPage = lazyNamed<{ handle: string }>(() => import("@/pages/PublicAffiliateJoinPage"), "PublicAffiliateJoinPage");
const PublicAffiliateInvitePage = lazyNamed<{ code: string }>(() => import("@/pages/PublicAffiliateJoinPage"), "PublicAffiliateInvitePage");
function AffiliateInviteRoute() {
  const code = parseInvitePath(useLocation().pathname);
  return code ? <PublicAffiliateInvitePage code={code} /> : <NotFoundPage />;
}

/** A store's FYP profile, its Shop and a product: /@<slug>[/shop[/<product>]].
 *  React Router cannot match "@" inside a segment, so these paths go to the
 *  routes that would otherwise answer them (a team page, Not found), which hand
 *  them over here. A page handle can never hold "@", so nothing is shadowed. */
function FypAt({ fallback }: { fallback: ReactNode }) {
  const { pathname } = useLocation();
  const joinHandle = parseAffiliateJoinPath(pathname);
  if (joinHandle) return <PublicAffiliateJoinPage handle={joinHandle} />;
  const at = parseFypAtPath(pathname);
  return at ? <PublicFypProfilePage at={at} /> : <>{fallback}</>;
}
const FypPage = lazyNamed(() => import("@/pages/dashboard/FypPage"), "FypPage");
const BilikPage = lazyNamed(() => import("@/pages/dashboard/BilikPage"), "BilikPage");
const BilikSessionPage = lazyNamed(() => import("@/pages/dashboard/BilikSessionPage"), "BilikSessionPage");
const FypPrestasiPage = lazyNamed(() => import("@/pages/dashboard/FypPrestasiPage"), "FypPrestasiPage");
const SmartSetupPage = lazyNamed(() => import("@/pages/dashboard/SmartSetupPage"), "SmartSetupPage");
const PagesListPage = lazyNamed(() => import("@/pages/dashboard/PagesListPage"), "PagesListPage");
const PageEditorPage = lazyNamed(() => import("@/pages/dashboard/PageEditorPage"), "PageEditorPage");
const TeamsPage = lazyNamed(() => import("@/pages/dashboard/TeamsPage"), "TeamsPage");
const ProductsPage = lazyNamed(() => import("@/pages/dashboard/ProductsPage"), "ProductsPage");
const CommunityPage = lazyNamed(() => import("@/pages/dashboard/CommunityPage"), "CommunityPage");
const BonusReviewPage = lazyNamed(() => import("@/pages/dashboard/BonusReviewPage"), "BonusReviewPage");
const OrdersPage = lazyNamed(() => import("@/pages/dashboard/OrdersPage"), "OrdersPage");
const AbandonedCheckoutsPage = lazyNamed(
  () => import("@/pages/dashboard/AbandonedCheckoutsPage"),
  "AbandonedCheckoutsPage",
);
const CustomersPage = lazyNamed(() => import("@/pages/dashboard/CustomersPage"), "CustomersPage");
const PetiMesejPage = lazyNamed(() => import("@/pages/dashboard/PetiMesejPage"), "PetiMesejPage");
const ChatResumePage = lazyNamed(() => import("@/pages/public/ChatResumePage"), "ChatResumePage");
const IklanDoorPage = lazyNamed(() => import("@/pages/public/IklanDoorPage"), "IklanDoorPage");
const DiscountsPage = lazyNamed(() => import("@/pages/dashboard/DiscountsPage"), "DiscountsPage");
const IklanPage = lazyNamed(() => import("@/pages/dashboard/IklanPage"), "IklanPage");
const AccessPage = lazyNamed(() => import("@/pages/dashboard/AccessPage"), "AccessPage");
const SettingsPage = lazyNamed(() => import("@/pages/dashboard/SettingsPage"), "SettingsPage");
const BillingPage = lazyNamed(() => import("@/pages/dashboard/BillingPage"), "BillingPage");
const SupportPage = lazyNamed(() => import("@/pages/dashboard/SupportPage"), "SupportPage");
const AffiliateManagePage = lazyNamed(() => import("@/pages/dashboard/AffiliateManagePage"), "AffiliateManagePage");
const FollowUpPage = lazyNamed(() => import("@/pages/dashboard/FollowUpPage"), "FollowUpPage");
const BioWebPage = lazyNamed(() => import("@/pages/dashboard/BioWebPage"), "BioWebPage");

// Props-typed: the apex root renders it directly with an already-resolved page.
const PublicPageView = lazyBuyer<{
  slug?: string;
  handle?: string;
  initialData?: PublicPage | null;
}>(() => import("@/pages/public/PublicPageView"), "PublicPageView");
const PublicCheckoutView = lazyBuyer(() => import("@/pages/public/PublicCheckoutView"), "PublicCheckoutView");
const PublicAccessView = lazyNamed(() => import("@/pages/public/PublicAccessView"), "PublicAccessView");
const ThankYouPage = lazyBuyer(() => import("@/pages/public/ThankYouPage"), "ThankYouPage");
const ShopPage = lazyNamed(() => import("@/pages/public/ShopPage"), "ShopPage");
const MarketplacePage = lazyNamed(() => import("@/pages/public/MarketplacePage"), "MarketplacePage");
const PlusLandingPage = lazyNamed(() => import("@/pages/public/PlusLandingPage"), "PlusLandingPage");
const PlusSubscribedPage = lazyNamed(() => import("@/pages/public/PlusSubscribedPage"), "PlusSubscribedPage");
const ChangelogPage = lazyNamed(() => import("@/pages/public/ChangelogPage"), "ChangelogPage");
const LegalPage = lazyNamed<{ doc: "privasi" | "terma" | "padam-data" }>(() => import("@/pages/public/LegalPage"), "LegalPage");
const DocumentationPage = lazyNamed(
  () => import("@/pages/public/DocumentationPage"),
  "DocumentationPage",
);
const ShopProductPage = lazyNamed(() => import("@/pages/public/ShopProductPage"), "ShopProductPage");
const IklanProductPage = lazyNamed(() => import("@/pages/public/IklanProductPage"), "IklanProductPage");
const StoreHome = lazyBuyer<{ bioOnly?: boolean }>(() => import("@/pages/public/StoreHome"), "StoreHome");
const ResellerPlanPage = lazyNamed(
  () => import("@/pages/public/ResellerPlanPage"),
  "ResellerPlanPage",
);
const StoreCheckout = lazyBuyer(() => import("@/pages/public/StoreCheckout"), "StoreCheckout");
const StoreProductPage = lazyBuyer(() => import("@/pages/public/StoreProductPage"), "StoreProductPage");
const NotFoundPage = lazyBuyer(() => import("@/pages/NotFoundPage"), "NotFoundPage");

// Boot-time Suspense fallback for the top-level route trees (apex + storefront)
// and HomeRoute. Renders the shared full-screen spinner so it lands exactly where
// index.html's #app-boot loader was — no size/position jump on handoff. (In-shell
// tab switches use PageSkeleton instead; see AppLayout/AdminLayout.)
const PageFallback = () => {
  const { pathname } = window.location;
  // A store root that is its FYP feed (server/lib/fypHomeEdge.ts) paints the same still.
  const fyp = pathname.startsWith("/fyp/") || (pathname === "/" && !!document.documentElement.dataset.fypPoster);
  return fyp ? <FypBootFallback /> : <FullScreenSpinner />;
};

const EarlyFunnelFyp = lazyBuyer<{ boot: FunnelBoot & { fyp: PublicFunnelFyp } }>(() => import("@/components/fyp/EarlyFunnelFyp"), "EarlyFunnelFyp");

/**
 * A FYP funnel's page that the Worker split (#funnel-boot carrying the feed,
 * server/lib/funnelEdge.ts): its videos play at once, before any route resolves
 * or the page is read. Every other address takes the usual routes, and so does
 * this one once the visitor has gone on to its sales page, or when it is the
 * sales page in a sheet over a FYP post's videos.
 */
function FunnelBootGate({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const b = funnelBoot();
  if (b?.fyp && b.feed && pathname === b.path && !salesOpenedAfterFyp(b.page) && !inSheet()) {
    return (
      <Suspense fallback={<FypBootFallback />}>
        <EarlyFunnelFyp boot={{ ...b, fyp: b.fyp }} />
      </Suspense>
    );
  }
  return <>{children}</>;
}

/**
 * Apex root ("/"): renders the landing page, the sales page, or one of the
 * super-admin's landing-library pages, per their choice. Paints instantly from the
 * localStorage-cached choice, then reconciles with the server on boot
 * (self-corrects within one fetch after the operator switches it). A library page
 * carries no cached payload, so that choice waits one fetch before painting.
 */
function HomeRoute() {
  const [res, setRes] = useState<HomepageResolution | undefined>(() => cachedHomepage());

  useEffect(() => {
    let alive = true;
    void ensureHomepage().then((h) => {
      if (alive) setRes(h);
    });
    return () => {
      alive = false;
    };
  }, []);

  if (!res) return <PageFallback />;
  // Whichever homepage it is, it sells: Bantuan opens here only from a buyer's link, never as a button (120M).
  const help = bantuanAsked(window.location.search) && (
    <Suspense fallback={null}>
      <BantuanChat surface="home" />
    </Suspense>
  );
  if (res.homepage === "page" && res.page) {
    // slug is passed so PublicPageView treats this as an already-resolved root
    // (no second fetch, and no redirect back to "/" — see its is_home guard).
    return (
      <>
        <PublicPageView slug={res.page.slug} initialData={res.page} />
        {help}
      </>
    );
  }
  return (
    <>
      {res.homepage === "sales" ? <SalesPage /> : <LandingPage />}
      {help}
    </>
  );
}

export function App() {
  // One Pages project serves every host: the apex/app host runs the dashboard +
  // marketing, while a seller subdomain (<sub>.<apex>) runs only that store's
  // public storefront. The dashboard/admin/auth trees are intentionally NOT
  // mounted on a storefront host.
  return <FunnelBootGate>{isStorefrontHost() ? <StorefrontApp /> : <ApexApp />}</FunnelBootGate>;
}

function ApexApp() {
  return (
    <Suspense fallback={<PageFallback />}>
      {/* Platform-wide pixel: PageView on every apex navigation (funnel tracking). */}
      <PlatformPixel />
      <Routes>
        <Route path="/" element={<HomeRoute />} />
        <Route path="/sales" element={<SalesPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/contact" element={<ContactPage />} />
        {/* Bilik Coaching Ubai (119K): the invite link and the room, apex only. */}
        <Route path="/coaching" element={<CoachingRoomPage />} />
        <Route path="/coaching/:code" element={<CoachingJoinPage />} />
        <Route path="/kemaskini" element={<ChangelogPage />} />
        {/* Privacy, terms, data deletion: the URLs Meta needs before the app can go Live. */}
        <Route path="/privasi" element={<LegalPage doc="privasi" />} />
        <Route path="/privacy" element={<LegalPage doc="privasi" />} />
        <Route path="/terma" element={<LegalPage doc="terma" />} />
        <Route path="/terms" element={<LegalPage doc="terma" />} />
        <Route path="/padam-data" element={<LegalPage doc="padam-data" />} />
        <Route path="/data-deletion" element={<LegalPage doc="padam-data" />} />
        <Route path="/documentation" element={<DocumentationPage />} />
        <Route path="/documentation/:chapter" element={<DocumentationPage />} />
        <Route path="/projek" element={<ProjectsPage />} />
        <Route path="/projek/:slug" element={<ProjectRegisterPage />} />
        {/* A seller's Go Live board, public: no login, read-only. */}
        <Route path="/live/:slug" element={<PublicLivePage />} />
        {/* Bilik Kelas door: the class link shared in WhatsApp; signs in on its own. */}
        <Route path="/k/:code" element={<KelasRoomPage />} />
        {/* A store's FYP feed, public: the address ads point at, opened on one video. */}
        <Route path="/j/:code" element={<AffiliateInviteRoute />} />
        <Route path="/fyp/:slug" element={<PublicFypPage />} />
        <Route path="/fyp/:slug/:code" element={<PublicFypPage />} />
        <Route path="/awam" element={<FypAwamPage />} />
        <Route path="/awam/:code" element={<FypAwamPage />} />
        {/* Public "Langgan terus": create account + pay in one step. */}
        <Route path="/subscribe" element={<SubscribePage />} />
        <Route path="/langgan/:resellerId" element={<ResellerBuyPage />} />
        <Route path="/subscribed/:id" element={<SubscribedPage />} />
        {/* Store-membership invite link — accessible signed in or out. */}
        <Route path="/invite/:token" element={<InvitePage />} />
        {/* OAuth consent for MCP connectors (claude.ai, ChatGPT). Signs the seller
            in itself, outside the guards, so it can carry the app's request through
            the login and back. */}
        <Route path="/oauth/authorize" element={<OAuthAuthorizePage />} />

        {/* Public auth pages */}
        <Route element={<PublicOnlyRoute />}>
          <Route path="/login" element={<LoginPage />} />
        </Route>
        {/* Registration sits OUTSIDE PublicOnlyRoute: a signed-in buyer becoming a
            seller finishes the upgrade on this very page, so bouncing signed-in
            users away would close the only door into it. The page itself sends an
            account that is already a seller on to the dashboard. */}
        <Route path="/register" element={<RegisterPage />} />
        {/* Pembantu Sellhiz: open a store or sign in by chat, with an emailed code (116F S7). */}
        <Route path="/daftar" element={<JoinChatPage />} />
        <Route path="/chat/:token" element={<ChatResumePage />} />
        {/* The private advertiser door (117C S8): invitation only, noindex. */}
        <Route path="/iklan" element={<IklanDoorPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        {/* After login: an account that is more than one of seller, buyer and
            affiliate picks where to go. Guards itself; single-role accounts pass
            straight through. */}
        <Route path="/masuk" element={<RoleChooserPage />} />
        {/* Password reset lives outside PublicOnlyRoute: the recovery link creates a
            session, which would otherwise bounce the user off /reset-password. */}
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Dashboard (protected) */}
        <Route element={<ProtectedRoute />}>
          <Route path="/dashboard" element={<AppLayout />}>
            <Route index element={<Navigate to="/dashboard/overview" replace />} />
            {/* Six doors. Features that used to own a sidebar row are tabs
                behind the door they belong to — the tab id lives in `?tab=`. */}
            <Route
              path="overview"
              element={
                <TabbedHub
                  doorTo="/dashboard/overview"
                  panes={{ ringkasan: OverviewPage, sedia: SmartSetupPage }}
                />
              }
            />
            <Route
              path="pages"
              element={
                <TabbedHub
                  doorTo="/dashboard/pages"
                  panes={{ senarai: PagesListPage, bio: BioWebPage }}
                />
              }
            />
            <Route path="pages/new" element={<PageEditorPage />} />
            <Route path="pages/:id/edit" element={<PageEditorPage />} />
            <Route
              path="products"
              element={
                <TabbedHub
                  doorTo="/dashboard/products"
                  panes={{ senarai: ProductsPage, review: BonusReviewPage }}
                />
              }
            />
            <Route
              path="orders"
              element={
                <TabbedHub
                  doorTo="/dashboard/orders"
                  panes={{
                    semua: OrdersPage,
                    troli: AbandonedCheckoutsPage,
                    akses: AccessPage,
                  }}
                />
              }
            />
            <Route path="community" element={<CommunityPage />} />
            <Route path="fyp" element={<FypPage />} />
            <Route path="bilik" element={<BilikPage />} />
            <Route path="bilik/sesi/:sessionId" element={<BilikSessionPage />} />
            <Route path="fyp/prestasi" element={<FypPrestasiPage />} />
            <Route path="customers" element={<CustomersPage />} />
            <Route path="peti-mesej" element={<PetiMesejPage />} />
            <Route
              path="marketing"
              element={
                <TabbedHub
                  doorTo="/dashboard/marketing"
                  panes={{
                    diskaun: DiscountsPage,
                    fup: FollowUpPage,
                    team: TeamsPage,
                    iklan: IklanPage,
                  }}
                  // Affiliate left Pemasaran for its own door; a bookmarked
                  // `?tab=affiliate` must land there, not silently on Diskaun.
                  movedTabs={{
                    affiliate: { to: "/dashboard/affiliate", notice: "nav.moved.affiliates" },
                  }}
                />
              }
            />
            <Route path="affiliate" element={<AffiliateManagePage />} />
            <Route path="support" element={<SupportPage />} />
            <Route path="settings" element={<SettingsPage />} />
            {/* Its own door, not a Tetapan tab: the renew path must stay a bare
                pathname (see BILLING_PATH) so a locked-out seller can reach it. */}
            <Route path="langganan" element={<BillingPage />} />

            {/* Old sidebar paths: still live, now redirecting to the tab that
                absorbed them, carrying a one-time "this moved" notice. */}
            {MOVED_ROUTES.map((r) => (
              <Route key={r.from} path={r.from} element={<MovedRoute to={r.to} notice={r.notice} />} />
            ))}
          </Route>
          {/* Full-screen live-sales view — no dashboard chrome, but still inside
              StoreProvider so it's store-scoped and sends the X-Store-Id header. */}
          <Route
            path="/live"
            element={
              <StoreProvider>
                <LiveSalesPage />
              </StoreProvider>
            }
          />
        </Route>

        {/* Operator console (super-admin) */}
        <Route element={<AdminRoute />}>
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Navigate to="/admin/overview" replace />} />
            <Route path="overview" element={<AdminOverviewPage />} />
            <Route path="resellers" element={<AdminResellersPage />} />
            <Route path="resellers/:id" element={<AdminResellerManagePage />} />
            <Route path="sellers" element={<AdminSellersPage />} />
            <Route path="sites" element={<AdminSitesPage />} />
            <Route path="sites/:id" element={<AdminSiteManagePage />} />
            <Route path="wp-import" element={<AdminWpImportPage />} />
            <Route path="page-insights" element={<AdminPageInsightsPage />} />
            <Route path="ads" element={<AdminAdsPage />} />
            <Route path="funnels" element={<AdminFunnelsPage />} />
            <Route path="funnels/new" element={<AdminFunnelNewPage />} />
            <Route path="funnels/:id" element={<AdminFunnelPage />} />
            <Route path="cashflow" element={<AdminCashflowPage />} />
            <Route path="monetize" element={<AdminMonetizePage />} />
            <Route path="ad-links" element={<AdminAdLinksPage />} />
            <Route path="domains" element={<AdminDomainsPage />} />
            <Route path="cf-accounts" element={<AdminCfAccountsPage />} />
            <Route path="orders" element={<AdminOrdersPage />} />
            <Route path="customers" element={<AdminCustomersPage />} />
            <Route path="payments" element={<AdminPaymentsPage />} />
            <Route path="plans" element={<AdminPlansPage />} />
            <Route path="membership" element={<AdminMembershipPage />} />
            <Route path="moderation" element={<AdminModerationPage />} />
            <Route path="support" element={<AdminSupportPage />} />
            <Route path="affiliates" element={<AdminAffiliatesPage />} />
            <Route path="audit" element={<AdminAuditPage />} />
            <Route path="settings" element={<AdminSettingsPage />} />
          </Route>
        </Route>

        {/* Reseller console (white-label mini-operator) */}
        <Route element={<ResellerRoute />}>
          <Route path="/reseller" element={<ResellerLayout />}>
            <Route index element={<Navigate to="/reseller/overview" replace />} />
            <Route path="overview" element={<ResellerOverviewPage />} />
            <Route path="sellers" element={<ResellerSellersPage />} />
            <Route path="site" element={<ResellerSitePage />} />
            <Route path="orders" element={<ResellerOrdersPage />} />
            <Route path="customers" element={<ResellerCustomersPage />} />
            <Route path="payment" element={<ResellerPaymentPage />} />
            <Route path="plans" element={<ResellerPlansPage />} />
          </Route>
        </Route>

        {/* Customer (buyer) area */}
        <Route path="/account/login" element={<AccountLoginPage />} />
        <Route path="/account/reset" element={<AccountResetPage />} />
        {/* Affiliate portal — any signed-in, verified account. Deliberately NOT
            behind CustomerRoute: that guard also demands a purchase, which locks
            an open-tier affiliate out of their own portal. The /affiliate URL is
            unchanged because it may be bookmarked.

            The index sits OUTSIDE the portal shell on purpose. It is the
            activation screen, shown to someone who has not joined — and while it
            lived inside AffiliateLayout it came wrapped in the very portal that
            person has no account for, sidebar and all ten links included.
            AffiliateRoute now guards the shell, so the menu only exists once
            there is something behind it. */}
        <Route element={<VerifiedRoute />}>
          <Route path="/affiliate">
            <Route index element={<AffiliatePage />} />
            <Route element={<AffiliateRoute />}>
              <Route element={<AffiliateLayout />}>
                <Route path="overview" element={<AffiliateOverviewPage />} />
                <Route path="links" element={<AffiliateLinksPage />} />
                <Route path="earnings" element={<AffiliateEarningsPage />} />
                <Route path="traffic" element={<AffiliateTrafficPage />} />
                <Route path="stores" element={<AffiliateStoresPage />} />
                <Route path="assets" element={<AffiliateAssetsPage />} />
                <Route path="leaderboard" element={<AffiliateLeaderboardPage />} />
                <Route path="terms" element={<AffiliateTermsPage />} />
                <Route path="pixel" element={<AffiliatePixelPage />} />
                <Route path="bank" element={<AffiliateBankPage />} />
              </Route>
            </Route>
          </Route>
        </Route>

        <Route element={<CustomerRoute />}>
          {/* A buyer still on the emailed starter password picks their own here
              first. Gated on the hub only — never on the access routes below, so
              a buyer who just paid isn't stopped on the way to their product. */}
          <Route
            path="/account"
            element={
              <SetPasswordGate>
                <AccountPage />
              </SetPasswordGate>
            }
          />
          <Route path="/account/certificate/:token" element={<AccountCertificatePage />} />
          {/* Ebook links land here when the ebook site has no session: log in, then straight into the ebook. */}
          <Route path="/account/ebook/:slug" element={<EbookOpenPage />} />
          <Route path="/learn/:token" element={<LmsView />} />
          <Route path="/learn/:token/certificate" element={<CertificatePage />} />
          {/* Digital access link — gated by login + ownership (the token alone,
              which is emailed and shareable, must not unlock content). */}
          <Route path="/access/:token" element={<PublicAccessView />} />

        </Route>

        {/* Cross-seller marketplace (prototype — mock data) */}
        <Route path="/jelajah" element={<MarketplacePage />} />

        {/* Sellhiz Plus — buyer membership */}
        <Route path="/plus" element={<PlusLandingPage />} />
        <Route path="/plus/subscribed/:id" element={<PlusSubscribedPage />} />

        {/* Public storefront */}
        <Route path="/shop/:storeSlug" element={<ShopPage />} />
        <Route path="/shop/:storeSlug/:productSlug" element={<ShopProductPage />} />
        <Route path="/iklan/p/:offerId" element={<IklanProductPage />} />

        {/* Public renderers */}
        <Route path="/p/:slug" element={<PublicPageView />} />
        {/* Dedicated WooCommerce/TikTok checkout URL for a page (real route). */}
        <Route path="/p/:slug/checkout" element={<PublicCheckoutView />} />
        <Route path="/thank-you/:orderId" element={<ThankYouPage />} />

        {/* Team-prefixed pages, e.g. /ubai/form. Kept last so it never shadows a
            static top-level route; reserved-word handles are blocked at creation. */}
        <Route path="/:handle/:slug" element={<FypAt fallback={<PublicPageView />} />} />
        <Route path="/:handle/:slug/checkout" element={<PublicCheckoutView />} />
        <Route path="/:at" element={<FypAt fallback={<NotFoundPage />} />} />
        <Route path="/:at/:shop/:product" element={<FypAt fallback={<NotFoundPage />} />} />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

/**
 * Route tree for a seller storefront host (<subdomain>.<apex>). Only public,
 * buyer-facing surfaces live here — pages, storefront landing, order/thank-you,
 * digital access, and the buyer account/LMS. PublicPageView resolves each page
 * scoped to the store that owns the subdomain (see currentSubdomain).
 *
 * React Router v6 ranks routes by specificity, so the static buyer routes always
 * win over the catch-all `/:slug` and `/:handle/:slug` page renderers.
 */
function StorefrontApp() {
  // Warm the buyer-funnel chunks (page/product renderers + checkout) so that by
  // the time the buyer taps a link or the checkout CTA the chunk is already
  // fetched. Deliberately keyed off the landing path and deferred past `load` —
  // on a checkout landing there is nothing downstream left to warm, and warming
  // it anyway delayed the page-data fetch the checkout is blocked on.
  useEffect(() => {
    prefetchBuyerFunnelOnIdle();
  }, []);

  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/" element={<StoreHome />} />

        {/* Buyer auth must run on the apex: Turnstile only authorises the apex
            hostname (a seller host errors 110200) and Supabase sessions are
            per-origin. ApexRedirect bounces these to the CURRENT apex — naming a
            domain here is what let this comment rot through the last move. */}
        <Route
          path="/account/login"
          element={
            <ApexRedirect>
              <AccountLoginPage />
            </ApexRedirect>
          }
        />
        <Route
          path="/account/reset"
          element={
            <ApexRedirect>
              <AccountResetPage />
            </ApexRedirect>
          }
        />
        <Route
          path="/k/:code"
          element={
            <ApexRedirect>
              <KelasRoomPage />
            </ApexRedirect>
          }
        />
        {/* Membership stays on the storefront host. The post-purchase funnel is
            deliberately same-origin: checkout → thank-you → /access, and the
            thank-you page silently signs the buyer in on THIS origin (see
            ThankYouPage AccountBlock), so these pages resolve with no login wall.
            Bouncing them to the apex would strand that per-origin session and
            reintroduce a login prompt right after paying. Only /account/login
            (above) must go to the apex, because Turnstile authorises the apex
            hostname alone. A buyer who is genuinely logged out here hits
            CustomerRoute → /account/login → apex, which is the intended path. */}
        {/* Affiliate portal — any signed-in, verified account. Deliberately NOT
            behind CustomerRoute: that guard also demands a purchase, which locks
            an open-tier affiliate out of their own portal. The /affiliate URL is
            unchanged because it may be bookmarked.

            The index sits OUTSIDE the portal shell on purpose. It is the
            activation screen, shown to someone who has not joined — and while it
            lived inside AffiliateLayout it came wrapped in the very portal that
            person has no account for, sidebar and all ten links included.
            AffiliateRoute now guards the shell, so the menu only exists once
            there is something behind it. */}
        <Route element={<VerifiedRoute />}>
          <Route path="/affiliate">
            <Route index element={<AffiliatePage />} />
            <Route element={<AffiliateRoute />}>
              <Route element={<AffiliateLayout />}>
                <Route path="overview" element={<AffiliateOverviewPage />} />
                <Route path="links" element={<AffiliateLinksPage />} />
                <Route path="earnings" element={<AffiliateEarningsPage />} />
                <Route path="traffic" element={<AffiliateTrafficPage />} />
                <Route path="stores" element={<AffiliateStoresPage />} />
                <Route path="assets" element={<AffiliateAssetsPage />} />
                <Route path="leaderboard" element={<AffiliateLeaderboardPage />} />
                <Route path="terms" element={<AffiliateTermsPage />} />
                <Route path="pixel" element={<AffiliatePixelPage />} />
                <Route path="bank" element={<AffiliateBankPage />} />
              </Route>
            </Route>
          </Route>
        </Route>

        <Route element={<CustomerRoute />}>
          {/* The persistent purchases library is canonical on the apex: buyers
              return to it days later from the confirmation email, where only the
              apex runs Turnstile login and holds their per-origin Supabase
              session. Its access/learn links are relative, so once the hub is on
              the apex they resolve there too. */}
          <Route
            path="/account"
            element={
              <ApexRedirect>
                <SetPasswordGate>
                  <AccountPage />
                </SetPasswordGate>
              </ApexRedirect>
            }
          />
          <Route path="/account/certificate/:token" element={<AccountCertificatePage />} />
          <Route path="/learn/:token" element={<LmsView />} />
          <Route path="/learn/:token/certificate" element={<CertificatePage />} />
          {/* Digital access link — stays same-origin so the post-purchase funnel
              (thank-you → access) opens with no login wall (the buyer is auto
              signed-in on THIS origin). Reached from the apex hub, the same
              relative link resolves on the apex. */}
          <Route path="/access/:token" element={<PublicAccessView />} />

        </Route>

        {/* Order + delivery surfaces. */}
        <Route path="/thank-you/:orderId" element={<ThankYouPage />} />

        {/* Page renderers, store-scoped. /p/<slug> keeps parity with apex links;
            /<slug> is the clean storefront form; /<handle>/<slug> for team prefixes.
            Each has a dedicated /checkout URL for the WooCommerce/TikTok styles. */}
        {/* Single-product checkout, reached from a bio-web products block. The
            static "product" segment out-ranks the /:slug page routes below. */}
        <Route path="/product/:slug" element={<StoreProductPage />} />
        {/* Iklan HQ: another store's advertised product, on this HQ store's own
            origin so its thank-you page can open it in a sheet. Three segments,
            so no page route below can shadow it. */}
        <Route path="/iklan/p/:offerId" element={<IklanProductPage />} />

        {/* Site-root checkout (WooCommerce convention): a sales-page button linking
            to /checkout?add-to-cart=<no> resolves the store's home page checkout.
            Static path, so it out-ranks the /:slug page renderer below. */}
        <Route path="/checkout" element={<StoreCheckout />} />

        {/* Reseller plan signup. It lives HERE rather than at the root so a
            reseller's storefront keeps selling their own products; the root is
            only replaced when they opt in. "plan" is a reserved slug, so this
            static segment can never shadow a real page. */}
        <Route path="/plan" element={<ResellerPlanPage />} />

        {/* The bio-web's permanent link: it answers here whatever the root is set
            to, so a seller can point Instagram at it and still swap the homepage.
            "bio" is a reserved page slug for the same reason as "plan". */}
        <Route path="/bio" element={<StoreHome bioOnly />} />

        {/* The store's FYP on its own host (111A): its root can be the feed or the
            profile (Tetapan › Homepage), and from there Home, Shop, Profil, a
            video's own link and a thank-you page's way back all stay here. */}
        <Route path="/j/:code" element={<AffiliateInviteRoute />} />
        <Route path="/fyp/:slug" element={<PublicFypPage />} />
        <Route path="/fyp/:slug/:code" element={<PublicFypPage />} />

        <Route path="/p/:slug" element={<PublicPageView />} />
        <Route path="/p/:slug/checkout" element={<PublicCheckoutView />} />
        <Route path="/:slug" element={<FypAt fallback={<PublicPageView />} />} />
        <Route path="/:slug/checkout" element={<PublicCheckoutView />} />
        <Route path="/:handle/:slug" element={<FypAt fallback={<PublicPageView />} />} />
        <Route path="/:handle/:slug/checkout" element={<PublicCheckoutView />} />
        <Route path="/:at/:shop/:product" element={<FypAt fallback={<NotFoundPage />} />} />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
